import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useGiveawayEntrants } from '@/hooks/queries'
import { notifyError, notifySuccess } from '@/lib/notify'
import { platformName } from '@/lib/text'
import { cn } from '@/lib/cn'
import { type Entrant, type EntrantAccount, winnersFirst } from '@/models/entrant'
import type { Giveaway } from '@/models/giveaway'
import { SkeletonLine } from '../ui/Skeleton'

const DATE = new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeStyle: 'short' })

function accountLabel(account: EntrantAccount): string {
  return account.display_name ?? account.account_id ?? 'Linked'
}

function matches(entrant: Entrant, query: string): boolean {
  const q = query.trim().toLowerCase()
  if (!q) return true
  return [
    entrant.username,
    entrant.nickname,
    entrant.email,
    ...entrant.accounts.flatMap((a) => [a.display_name, a.account_id]),
  ].some((value) => value?.toLowerCase().includes(q))
}

/** One CSV cell, quoted, with formula-starting values defused for spreadsheets. */
function csvCell(value: string | number | null | undefined): string {
  let text = value === null || value === undefined ? '' : String(value)
  if (/^[=+\-@\t\r]/.test(text)) text = `'${text}`
  return `"${text.replace(/"/g, '""')}"`
}

/** Lets Excel read the file as UTF-8, so non-English names survive. */
const BOM = String.fromCharCode(0xfeff)

function downloadCsv(giveaway: Giveaway, entrants: Entrant[]) {
  const platforms = [...new Set(entrants.flatMap((e) => e.accounts.map((a) => a.platform)))]
  const header = [
    'Username',
    'Nickname',
    'Email',
    'Won',
    'Prize keys',
    'Entries',
    'Entry options completed',
    'First entered',
    ...platforms.flatMap((p) => [`${platformName(p)} name`, `${platformName(p)} ID`]),
  ]
  const rows = entrants.map((e) => [
    e.username,
    e.nickname,
    e.email,
    e.won_at?.toISOString() ?? '',
    e.prize_keys.join(' '),
    e.entries,
    e.objectives,
    e.entered_at?.toISOString() ?? '',
    ...platforms.flatMap((p) => {
      const account = e.accounts.find((a) => a.platform === p)
      return [account?.display_name, account?.account_id]
    }),
  ])
  const csv = [header, ...rows].map((row) => row.map(csvCell).join(',')).join('\r\n')

  const url = URL.createObjectURL(new Blob([BOM + csv], { type: 'text/csv;charset=utf-8' }))
  const link = document.createElement('a')
  link.href = url
  link.download = `${giveaway.slug || `giveaway-${giveaway.id}`}-entrants.csv`
  link.click()
  setTimeout(() => URL.revokeObjectURL(url), 0)
}

function EntrantRow({ entrant }: { entrant: Entrant }) {
  const name = entrant.nickname || entrant.username || `User #${entrant.user_id}`
  return (
    <li className={cn('entrant', entrant.won && 'is-winner')}>
      <div className="entrant-main">
        <div className="entrant-names">
          {entrant.won ? <span className="entrant-winner-badge">Winner</span> : null}
          {entrant.username ? (
            <Link className="entrant-name" to={`/u/${entrant.username}`}>
              {name}
            </Link>
          ) : (
            <span className="entrant-name">{name}</span>
          )}
          {entrant.username && entrant.nickname ? (
            <span className="entrant-username">@{entrant.username}</span>
          ) : null}
        </div>
        {entrant.email ? (
          <a className="entrant-email" href={`mailto:${entrant.email}`}>
            {entrant.email}
          </a>
        ) : (
          <span className="entrant-email is-missing">No email</span>
        )}
        {entrant.prize_keys.length > 0 ? (
          <div className="entrant-prizes">
            {entrant.prize_keys.map((key) => (
              <code className="entrant-prize-key" key={key}>
                {key}
              </code>
            ))}
          </div>
        ) : null}
        {entrant.accounts.length > 0 ? (
          <div className="entrant-accounts">
            {entrant.accounts.map((account) => (
              <span
                className={cn('entrant-account', `is-${account.platform}`)}
                key={account.platform}
                title={
                  account.account_id
                    ? `${platformName(account.platform)} ID ${account.account_id}`
                    : undefined
                }
              >
                <span className="entrant-account-platform">{platformName(account.platform)}</span>
                {accountLabel(account)}
              </span>
            ))}
          </div>
        ) : null}
      </div>
      <div className="entrant-stats">
        <span className="entrant-entries">
          {entrant.entries.toLocaleString()} {entrant.entries === 1 ? 'entry' : 'entries'}
        </span>
        {entrant.entered_at ? (
          <time className="entrant-date" dateTime={entrant.entered_at.toISOString()}>
            {DATE.format(entrant.entered_at)}
          </time>
        ) : null}
      </div>
    </li>
  )
}

/**
 * The organiser's view of who entered: name, email and linked accounts, with
 * a search, a copy-emails shortcut and a CSV export. Once the giveaway has
 * ended it opens on its own with the winners at the top, marked with the key
 * each one got. Render only for the owner; the API refuses anyone else.
 */
export function GiveawayEntrants({ giveaway }: { giveaway: Giveaway }) {
  const ended = giveaway.has_ended
  const [open, setOpen] = useState(ended)
  const [query, setQuery] = useState('')
  const { data: entrants, isPending, isError, refetch } = useGiveawayEntrants(
    giveaway.id,
    open || ended,
  )

  const shown = useMemo(
    () => winnersFirst((entrants ?? []).filter((entrant) => matches(entrant, query))),
    [entrants, query],
  )
  const winnerCount = useMemo(() => (entrants ?? []).filter((e) => e.won).length, [entrants])

  const copyEmails = async () => {
    const emails = [...new Set(shown.map((e) => e.email).filter((e): e is string => !!e))]
    if (emails.length === 0) return
    try {
      await navigator.clipboard.writeText(emails.join(', '))
      notifySuccess(`Copied ${emails.length} ${emails.length === 1 ? 'email' : 'emails'}`)
    } catch {
      notifyError("Your browser didn't allow copying. Try the CSV download instead.")
    }
  }

  const count = entrants?.length ?? null

  return (
    <section className="giveaway-entrants" aria-labelledby="giveaway-entrants-title">
      <button
        type="button"
        className="giveaway-entrants-toggle"
        aria-expanded={open}
        aria-controls="giveaway-entrants-body"
        onClick={() => setOpen((value) => !value)}
      >
        <span id="giveaway-entrants-title" className="giveaway-entrants-title">
          Entrants
          {count !== null ? <span className="giveaway-entrants-count">{count}</span> : null}
        </span>
        {ended && entrants ? (
          <span className="giveaway-entrants-winners">
            {winnerCount === 0
              ? 'No winners'
              : `${winnerCount} ${winnerCount === 1 ? 'winner' : 'winners'}`}
          </span>
        ) : null}
        <span className="giveaway-entrants-hint">Only you can see this</span>
        <svg
          className={cn('giveaway-entrants-chevron', open && 'open')}
          viewBox="0 0 16 16"
          aria-hidden="true"
        >
          <path d="M4 6l4 4 4-4" />
        </svg>
      </button>

      {open ? (
        <div className="giveaway-entrants-body" id="giveaway-entrants-body">
          {isPending ? (
            <ul className="entrant-list" aria-hidden="true">
              {[0, 1, 2].map((key) => (
                <li className="entrant" key={key}>
                  <div className="entrant-main">
                    <SkeletonLine width="9em" />
                    <SkeletonLine width="14em" />
                  </div>
                </li>
              ))}
            </ul>
          ) : isError ? (
            <div className="empty-state">
              <div className="empty-state-title">Couldn't load entrants</div>
              <div className="empty-state-body">
                <button type="button" className="link-button" onClick={() => void refetch()}>
                  Try again
                </button>
              </div>
            </div>
          ) : entrants.length === 0 ? (
            <div className="empty-state">
              <div className="empty-state-title">No entrants yet</div>
              <div className="empty-state-body">Share the giveaway to get your first entries.</div>
            </div>
          ) : (
            <>
              <div className="giveaway-entrants-tools">
                <input
                  className="giveaway-entrants-search"
                  type="search"
                  placeholder="Search name, email or Discord"
                  aria-label="Search entrants"
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                />
                <button type="button" className="media-action" onClick={() => void copyEmails()}>
                  Copy emails
                </button>
                <button
                  type="button"
                  className="media-action"
                  onClick={() => downloadCsv(giveaway, shown)}
                >
                  Download CSV
                </button>
              </div>
              {shown.length === 0 ? (
                <div className="giveaway-entrants-none">No entrants match “{query}”.</div>
              ) : (
                <ul className="entrant-list">
                  {shown.map((entrant) => (
                    <EntrantRow entrant={entrant} key={entrant.user_id} />
                  ))}
                </ul>
              )}
            </>
          )}
        </div>
      ) : null}
    </section>
  )
}
